/**
 * Asked on the way out when this puzzle is not being autosaved — the user
 * already has a draft for another puzzle, and there is only one draft slot.
 * Leaving must never drop what was written without saying so.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton, SecondaryButton } from "./widgets";
import { C, FONT, TINT } from "./tokens";

export default function ExitSheet({ name, onSave, onDiscard, onStay }) {
  const { t: tr } = useTranslation();
  const saveRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onStay(); };
    window.addEventListener("keydown", onKey);
    if (saveRef.current) saveRef.current.querySelector("button")?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onStay]);

  return (
    <div
      data-testid="creator-exit-sheet"
      style={{ position: "absolute", inset: 0, zIndex: 20, display: "flex", flexDirection: "column", justifyContent: "flex-end", fontFamily: FONT }}
    >
      <button
        type="button"
        aria-label={tr("app.creator.exit.stay")}
        onClick={onStay}
        style={{ position: "absolute", inset: 0, border: "none", padding: 0, background: TINT.shadow(0.38), cursor: "pointer" }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="creator-exit-title"
        style={{
          position: "relative", background: C.surface, borderRadius: "28px 28px 0 0",
          padding: "26px 22px calc(env(safe-area-inset-bottom, 0px) + 22px)",
          boxShadow: `0 -18px 40px -20px ${TINT.shadow(0.45)}`,
        }}
      >
        <h2 id="creator-exit-title" style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" }}>
          {name ? tr("app.creator.exit.heading", { name }) : tr("app.creator.exit.headingPlain")}
        </h2>
        <p style={{ margin: "8px 0 20px", fontSize: 15, lineHeight: 1.45, color: C.body }}>
          {tr("app.creator.exit.body")}
        </p>
        <div ref={saveRef} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <PrimaryButton testId="creator-exit-save" icon={null} onClick={onSave}>
            {tr("app.creator.exit.save")}
          </PrimaryButton>
          <SecondaryButton testId="creator-exit-stay" onClick={onStay}>
            {tr("app.creator.exit.stay")}
          </SecondaryButton>
          <button
            type="button"
            data-testid="creator-exit-discard"
            onClick={onDiscard}
            style={{ height: 44, border: "none", background: "transparent", color: C.muted, fontFamily: FONT, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            {tr("app.creator.exit.discard")}
          </button>
        </div>
      </div>
    </div>
  );
}
